import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { ballRadius } from '../sim/world.js';
import type { PlayMode } from '../sim/world.js';
import { createProjectedMaterial } from '../render/ball-material-bridge.js';
import { BALL_COLORS } from '../render/balls.js';
import {
  type RackableMode,
  type RackConfig,
  type RackTab,
  type SlotOffsets,
  type ResolvedBall,
  RACK_OFFSET_LIMIT,
  MAX_CUSTOM_TABS,
  BASIC_TAB_ID,
  rackSlots,
  resolveRack,
  neighborSlotPairs,
  defaultOrder,
  nudgeBack,
  nudgeApex,
  nudgeSides,
  swapNumbers,
  loadRackConfig,
  saveRackConfig,
  nextTabId,
} from '../sim/rack-layout.js';

/**
 * ラック調整エディタ（3D・Three.js・スロットモデル）。
 *
 * 真上ビューでラックを表示。球タップ→別球タップで番号入替（1番/中央キー球はロック）、
 * 非ロック球のドラッグで位置オフセット、背景ドラッグでパン、ピンチでズーム。
 * 隙間は発光ブリッジ（黄→赤）で可視化、選択球以外は半透明。
 * 全体ナッジ: 下から押し上げ/上から押し下げ/横から挟む/完全タイトに戻す。タブ（最大5）。
 *
 * webgl-3d 準拠: 専用 renderer/scene を分離、表示中のみ描画ループ（show/hide）。
 * 球材はキャッシュ共有のため clone（per-ball 不透明度をゲーム側へ波及させない）。
 */

export interface RackEditorOpts {
  readonly getMode: () => PlayMode;
  readonly onChange: () => void;
}

export interface RackEditor {
  readonly element: HTMLElement;
  show: () => void;
  hide: () => void;
}

const DIM_OPACITY = 0.35;
const TAP_PX = 6; // これ未満の移動はタップ扱い
const GAP_MIN = 0.0006; // これ以上を「隙間あり」とみなす [m]
const GAP_REF = ballRadius * 0.6; // 赤マーカーが最大サイズになる隙間幅

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string,
  parent: HTMLElement,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  node.className = cls;
  parent.appendChild(node);
  return node;
};

export const createRackEditor = (opts: RackEditorOpts): RackEditor => {
  const root = document.createElement('div');
  root.className = 'pool-rack-editor';

  const tabbar = el('div', 'pool-rack-tabs', root);
  const note = el('div', 'pool-rack-note', root);
  const practiceMsg = el('div', 'pool-rack-practice', root);
  practiceMsg.textContent = 'プラクティスはラック調整の対象外です。';
  practiceMsg.hidden = true;

  // 凡例: マーカー色の意味（緑=密着 / 赤=隙間あり）。
  const legend = el('div', 'pool-rack-legend', root);
  const legendItem = (color: string, label: string): void => {
    const item = el('span', 'pool-rack-legend-item', legend);
    el('span', 'pool-rack-legend-dot', item).style.background = color;
    el('span', 'pool-rack-legend-label', item).textContent = label;
  };
  legendItem('#39e06a', '密着');
  legendItem('#ff2a2a', '隙間あり');

  const viewport = el('div', 'pool-rack-viewport', root);
  const canvas = el('canvas', 'pool-rack-canvas', viewport);

  // ツールはアイコン1列（ラベルは title / aria-label）。
  const tools = el('div', 'pool-rack-tools', root);
  const toolBtn = (icon: string, label: string, danger = false): HTMLButtonElement => {
    const b = el('button', `pool-rack-tool${danger ? ' is-danger' : ''}`, tools);
    b.textContent = icon;
    b.title = label;
    b.setAttribute('aria-label', label);
    return b;
  };
  const pushUpBtn = toolBtn('⬆', '下から押し上げ');
  const pushDownBtn = toolBtn('⬇', '上から押し下げ（1番）');
  const squeezeBtn = toolBtn('»«', '横から挟む');
  const tightBtn = toolBtn('⊙', '完全タイトに戻す');
  const deleteBtn = toolBtn('🗑', 'このラックを削除', true);

  // ---- 状態 ----
  let mode: RackableMode | null = null;
  let config: RackConfig = { activeId: BASIC_TAB_ID, tabs: [] };
  let active: RackTab | null = null;
  let selectedSlot: number | null = null;
  let zoom = 1;
  let fitDistance = 1;

  // ---- Three.js ----
  let renderer: THREE.WebGLRenderer | null = null;
  let scene: THREE.Scene | null = null;
  let camera: THREE.PerspectiveCamera | null = null;
  let pmrem: THREE.PMREMGenerator | null = null;
  let running = false;
  let rafId = 0;
  const sphereGeo = new THREE.IcosahedronGeometry(ballRadius, 3);
  const ballMeshes = new Map<number, THREE.Mesh>(); // slotIndex → mesh
  const markerGroup = new THREE.Group(); // 接触/隙間マーカー
  const markerGeo = new THREE.SphereGeometry(1, 16, 12); // 単位球（scale で大きさ調整）
  const center = new THREE.Vector3();
  const raycaster = new THREE.Raycaster();
  const dragPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  let felt: THREE.Mesh | null = null;

  const editable = (): boolean => active !== null && !active.readonly;

  const ensureThree = (): void => {
    if (renderer) return;
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    scene = new THREE.Scene();
    pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = 0.5;
    camera = new THREE.PerspectiveCamera(36, 1, 0.01, 100);
    camera.up.set(-1, 0, 0); // 真上ビュー: apex(小x) を画面上に
    scene.add(new THREE.AmbientLight(0xffffff, 0.45));
    const key = new THREE.DirectionalLight(0xffffff, 1.3);
    key.position.set(0.4, -0.8, 2.5);
    scene.add(key);
    scene.add(markerGroup);
  };

  const buildFelt = (): void => {
    if (!scene || !mode) return;
    if (felt) {
      scene.remove(felt);
      felt.geometry.dispose();
      (felt.material as THREE.Material).dispose();
    }
    // 暗ニュートラル背景＋暗いフェルトで明るい球を引き立てる。隙間はマーカーで明示。
    scene.background = new THREE.Color(0x15181d);
    const geo = new THREE.PlaneGeometry(1.0, 1.0);
    const mat = new THREE.MeshStandardMaterial({ color: 0x2a2f37, roughness: 0.92 });
    felt = new THREE.Mesh(geo, mat);
    felt.position.set(center.x, center.y, -ballRadius);
    scene.add(felt);
  };

  // ---- 球メッシュ ----
  const disposeBalls = (): void => {
    if (!scene) return;
    for (const m of ballMeshes.values()) {
      scene.remove(m);
      const mat = m.material;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else mat.dispose();
    }
    ballMeshes.clear();
  };

  const buildBalls = (): void => {
    if (!scene || !mode || !active) return;
    disposeBalls();
    const resolved = resolveRack(mode, active);
    rackSlots(mode).forEach((s, i) => {
      const num = resolved[i]!.number;
      const color = new THREE.Color(BALL_COLORS[num] ?? 0xffffff);
      const mat = (
        createProjectedMaterial(num, color) as THREE.MeshPhysicalMaterial
      ).clone();
      mat.transparent = true;
      const mesh = new THREE.Mesh(sphereGeo, mat);
      mesh.quaternion.random();
      scene!.add(mesh);
      ballMeshes.set(s.index, mesh);
    });
  };

  const currentResolved = (): ResolvedBall[] =>
    mode && active ? resolveRack(mode, active) : [];

  // 隣接ペアの接触/隙間マーカー。緑=密着 / 赤(大きいほど隙間大)=隙間あり。常に最前面。
  const refreshMarkers = (): void => {
    if (!mode) return;
    for (const c of [...markerGroup.children]) {
      markerGroup.remove(c);
      if (c instanceof THREE.Mesh) (c.material as THREE.Material).dispose();
    }
    const resolved = currentResolved();
    for (const [a, b] of neighborSlotPairs(mode)) {
      const pa = resolved[a];
      const pb = resolved[b];
      if (!pa || !pb) continue;
      const gap = Math.hypot(pa.x - pb.x, pa.y - pb.y) - 2 * ballRadius;
      const hasGap = gap > GAP_MIN;
      const t = Math.min(1, gap / GAP_REF);
      const mat = new THREE.MeshBasicMaterial({
        color: hasGap ? new THREE.Color(0xff2a2a) : new THREE.Color(0x39e06a),
        transparent: true,
        opacity: hasGap ? 0.95 : 0.7,
        depthTest: false,
        depthWrite: false,
      });
      const m = new THREE.Mesh(markerGeo, mat);
      m.renderOrder = 999;
      const r = hasGap
        ? ballRadius * (0.26 + 0.45 * t) // 隙間が広いほど大きい赤マーカー
        : ballRadius * 0.16; // 密着は小さい緑点
      m.scale.setScalar(r);
      m.position.set((pa.x + pb.x) / 2, (pa.y + pb.y) / 2, ballRadius * 1.05);
      markerGroup.add(m);
    }
  };

  const refreshTransforms = (): void => {
    const resolved = currentResolved();
    rackSlots(mode!).forEach((s, i) => {
      const mesh = ballMeshes.get(s.index);
      const r = resolved[i];
      if (!mesh || !r) return;
      mesh.position.set(r.x, r.y, 0);
      const mat = mesh.material as THREE.MeshPhysicalMaterial;
      mat.opacity =
        selectedSlot === null || selectedSlot === s.index ? 1 : DIM_OPACITY;
    });
    refreshMarkers();
  };

  // ---- カメラ ----
  const frameCamera = (): void => {
    if (!camera || !mode) return;
    const slots = rackSlots(mode);
    const xs = slots.map((s) => s.x);
    const ys = slots.map((s) => s.y);
    center.set(
      (Math.min(...xs) + Math.max(...xs)) / 2,
      (Math.min(...ys) + Math.max(...ys)) / 2,
      0,
    );
    // 真上ビュー(up=-x): 画面縦=x方向 / 画面横=y方向。縦横それぞれが収まる距離の大きい方。
    const margin = ballRadius * 2 + RACK_OFFSET_LIMIT * 2;
    const xExtent = Math.max(...xs) - Math.min(...xs) + margin;
    const yExtent = Math.max(...ys) - Math.min(...ys) + margin;
    const vTan = Math.tan((camera.fov * Math.PI) / 360);
    const hTan = vTan * camera.aspect;
    const dV = xExtent / 2 / vTan;
    const dH = yExtent / 2 / hTan;
    fitDistance = Math.max(dV, dH) * 1.15;
    zoom = 1;
    applyCamera();
  };

  const applyCamera = (): void => {
    if (!camera) return;
    camera.position.set(center.x, center.y, fitDistance * zoom);
    camera.lookAt(center);
  };

  const resize = (): void => {
    if (!renderer || !camera) return;
    const w = viewport.clientWidth || 240;
    const h = viewport.clientHeight || 260;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };

  const renderLoop = (): void => {
    if (!running || !renderer || !scene || !camera) return;
    renderer.render(scene, camera);
    rafId = requestAnimationFrame(renderLoop);
  };

  // ---- ポインタ ----
  const pointers = new Map<number, { x: number; y: number }>();
  let pinchDist = 0;
  type Inter = { kind: 'press' | 'drag' | 'pan'; slot: number | null };
  let inter: Inter | null = null;
  const downXY = new THREE.Vector2();
  const dragStartWorld = new THREE.Vector3();
  let dragStartOx = 0;
  let dragStartOy = 0;
  const panGrab = new THREE.Vector3();

  const ndc = (cx: number, cy: number): THREE.Vector2 => {
    const r = canvas.getBoundingClientRect();
    return new THREE.Vector2(
      ((cx - r.left) / r.width) * 2 - 1,
      -((cy - r.top) / r.height) * 2 + 1,
    );
  };
  const planePoint = (cx: number, cy: number): THREE.Vector3 | null => {
    if (!camera) return null;
    raycaster.setFromCamera(ndc(cx, cy), camera);
    const hit = new THREE.Vector3();
    return raycaster.ray.intersectPlane(dragPlane, hit) ? hit : null;
  };
  const pickSlot = (cx: number, cy: number): number | null => {
    if (!camera) return null;
    raycaster.setFromCamera(ndc(cx, cy), camera);
    const hits = raycaster.intersectObjects([...ballMeshes.values()], false);
    if (hits.length === 0) return null;
    for (const [idx, mesh] of ballMeshes) if (mesh === hits[0]!.object) return idx;
    return null;
  };
  // ドラッグ位置を他球に重ならない位置へ押し出す（接触まで寄せて隙間を詰められる）。
  const seat = (slot: number, x: number, y: number): { x: number; y: number } => {
    const resolved = currentResolved();
    const others = rackSlots(mode!)
      .map((s, i) => ({ index: s.index, r: resolved[i]! }))
      .filter((o) => o.index !== slot);
    const min = 2 * ballRadius;
    let px = x;
    let py = y;
    for (let it = 0; it < 8; it++) {
      for (const o of others) {
        let dx = px - o.r.x;
        let dy = py - o.r.y;
        let d = Math.hypot(dx, dy);
        if (d >= min) continue;
        if (d < 1e-6) {
          dx = min;
          dy = 0;
          d = min;
        }
        const push = (min - d) / d;
        px += dx * push;
        py += dy * push;
      }
    }
    return { x: px, y: py };
  };
  const clampOff = (v: number): number =>
    Math.max(-RACK_OFFSET_LIMIT, Math.min(RACK_OFFSET_LIMIT, v));

  const persist = (): void => {
    if (!mode) return;
    saveRackConfig(mode, config);
    opts.onChange();
  };

  const isLocked = (slot: number): boolean =>
    !!rackSlots(mode!).find((s) => s.index === slot)?.locked;

  const handleTap = (slot: number | null): void => {
    if (!editable()) {
      selectedSlot = null;
      refreshTransforms();
      return;
    }
    if (slot === null || isLocked(slot)) {
      selectedSlot = null;
    } else if (selectedSlot === null) {
      selectedSlot = slot;
    } else if (selectedSlot === slot) {
      selectedSlot = null;
    } else {
      // 2球目タップ → 番号入替（両方非ロック時）。
      active!.order = swapNumbers(mode!, active!.order, selectedSlot, slot);
      selectedSlot = null;
      buildBalls(); // 番号が変わったので作り直す
      persist();
    }
    refreshTransforms();
  };

  canvas.addEventListener('pointerdown', (e) => {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) {
      const pts = [...pointers.values()];
      pinchDist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
      inter = null; // ピンチ優先
      return;
    }
    downXY.set(e.clientX, e.clientY);
    const slot = pickSlot(e.clientX, e.clientY);
    if (slot === null) {
      // 背景 → パン開始
      const wp = planePoint(e.clientX, e.clientY);
      if (wp) panGrab.copy(wp);
      inter = { kind: 'pan', slot: null };
    } else {
      inter = { kind: 'press', slot };
      if (editable() && !isLocked(slot)) {
        // ルール上の固定球（locked スロット＝9ボールなら①⑨）は位置ドラッグも禁止＝完全固定。
        // 非ロック球のみドラッグ開始位置を控える（位置調整・番号入替ともに非ロック球が対象）。
        const wp = planePoint(e.clientX, e.clientY);
        if (wp) {
          dragStartWorld.copy(wp);
          const o = active!.offsets[slot];
          dragStartOx = o?.x ?? 0;
          dragStartOy = o?.y ?? 0;
        }
      }
    }
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch (err) {
      console.warn('rack-editor: setPointerCapture failed', err);
    }
  });

  canvas.addEventListener('pointermove', (e) => {
    const prev = pointers.get(e.pointerId);
    if (prev) {
      prev.x = e.clientX;
      prev.y = e.clientY;
    }
    if (pointers.size === 2) {
      const pts = [...pointers.values()];
      const dist = Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
      if (pinchDist > 0) {
        zoom = Math.max(0.35, Math.min(3, zoom * (pinchDist / dist)));
        applyCamera();
      }
      pinchDist = dist;
      return;
    }
    if (!inter) return;
    if (inter.kind === 'pan') {
      const now = planePoint(e.clientX, e.clientY);
      if (now) {
        center.x += panGrab.x - now.x;
        center.y += panGrab.y - now.y;
        applyCamera();
      }
      return;
    }
    // press → drag 判定（非ロック球のみ。ルール固定球は位置移動も禁止）。
    const moved = Math.hypot(e.clientX - downXY.x, e.clientY - downXY.y) > TAP_PX;
    if (
      inter.kind === 'press' &&
      moved &&
      editable() &&
      inter.slot !== null &&
      !isLocked(inter.slot)
    ) {
      inter.kind = 'drag';
      selectedSlot = inter.slot;
    }
    if (inter.kind === 'drag' && inter.slot !== null) {
      const wp = planePoint(e.clientX, e.clientY);
      if (!wp) return;
      const s = rackSlots(mode!).find((x) => x.index === inter!.slot)!;
      // 目標位置を他球に重ならない位置へ押し出してから offset 化（接触まで寄せられる）。
      const seated = seat(
        inter.slot,
        s.x + dragStartOx + (wp.x - dragStartWorld.x),
        s.y + dragStartOy + (wp.y - dragStartWorld.y),
      );
      const offX = seated.x - s.x;
      const offY = seated.y - s.y;
      // オフセット箱(±R)内に収まる時のみ反映。箱外＝重なり回避に箱を超える必要がある位置なので、
      // clamp で引き戻すと重なりが復活する → このフレームの移動は破棄して直前の非重なりを維持。
      if (
        Math.abs(offX) <= RACK_OFFSET_LIMIT + 1e-9 &&
        Math.abs(offY) <= RACK_OFFSET_LIMIT + 1e-9
      ) {
        active!.offsets[inter.slot] = { x: clampOff(offX), y: clampOff(offY) };
        refreshTransforms();
      }
    }
  });

  const endPointer = (e: PointerEvent): void => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinchDist = 0;
    try {
      canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    if (!inter) return;
    if (inter.kind === 'drag') persist();
    else if (inter.kind === 'press') handleTap(inter.slot);
    inter = null;
  };
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);
  canvas.style.touchAction = 'none';

  // マウスホイールでズーム（タッチはピンチ）。
  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      zoom = Math.max(0.35, Math.min(3, zoom * (e.deltaY > 0 ? 1.12 : 0.89)));
      applyCamera();
    },
    { passive: false },
  );

  // ---- ツール ----
  const applyOffsets = (next: SlotOffsets): void => {
    if (!active || active.readonly) return;
    active.offsets = next;
    selectedSlot = null;
    refreshTransforms();
    persist();
  };
  pushUpBtn.addEventListener('click', () => {
    if (mode && editable()) applyOffsets(nudgeBack(mode, active!.offsets));
  });
  pushDownBtn.addEventListener('click', () => {
    if (mode && editable()) applyOffsets(nudgeApex(mode, active!.offsets));
  });
  squeezeBtn.addEventListener('click', () => {
    if (mode && editable()) applyOffsets(nudgeSides(mode, active!.offsets));
  });
  tightBtn.addEventListener('click', () => {
    if (editable()) applyOffsets({});
  });
  deleteBtn.addEventListener('click', () => {
    if (!mode || !active || active.readonly) return;
    config.tabs = config.tabs.filter((t) => t.id !== active!.id);
    config.activeId = BASIC_TAB_ID;
    saveRackConfig(mode, config);
    opts.onChange();
    selectTab(BASIC_TAB_ID);
    buildTabbar();
  });

  // ---- タブ ----
  const selectTab = (id: string): void => {
    if (!mode) return;
    config.activeId = id;
    active = config.tabs.find((t) => t.id === id) ?? config.tabs[0]!;
    selectedSlot = null;
    saveRackConfig(mode, config);
    syncToolState();
    buildBalls();
    refreshTransforms();
    opts.onChange();
  };

  const syncToolState = (): void => {
    const ed = editable();
    pushUpBtn.disabled = !ed;
    pushDownBtn.disabled = !ed;
    squeezeBtn.disabled = !ed;
    tightBtn.disabled = !ed;
    deleteBtn.hidden = !ed;
    note.textContent = ed
      ? '球をタップ→別の球をタップで番号入替。球をドラッグで位置調整（ルール上の固定球＝1番・中央球は移動・入替できません）。背景ドラッグで移動、ピンチ/ホイールで拡大。'
      : 'ベーシックは編集できません。＋で複製してオリジナルを作成してください。';
  };

  const buildTabbar = (): void => {
    tabbar.replaceChildren();
    for (const t of config.tabs) {
      const btn = el('button', 'pool-rack-tab', tabbar);
      btn.textContent = t.name;
      btn.classList.toggle('is-active', t.id === config.activeId);
      btn.addEventListener('click', () => {
        selectTab(t.id);
        buildTabbar();
      });
    }
    const customs = config.tabs.filter((t) => t.id !== BASIC_TAB_ID).length;
    if (customs < MAX_CUSTOM_TABS) {
      const add = el('button', 'pool-rack-tab pool-rack-tab-add', tabbar);
      add.textContent = '＋';
      add.title = 'ベーシックを複製して新規ラック';
      add.addEventListener('click', () => {
        if (!mode) return;
        const id = nextTabId(config);
        config.tabs.push({
          id,
          name: `ラック${customs + 1}`,
          readonly: false,
          offsets: {},
          order: defaultOrder(mode),
        });
        config.activeId = id;
        saveRackConfig(mode, config);
        selectTab(id);
        buildTabbar();
      });
    }
  };

  // ---- 表示 ----
  const refresh = (): void => {
    const m = opts.getMode();
    if (m === 'practice') {
      mode = null;
      practiceMsg.hidden = false;
      note.hidden = true;
      legend.hidden = true;
      viewport.hidden = true;
      tools.hidden = true;
      tabbar.hidden = true;
      return;
    }
    mode = m;
    practiceMsg.hidden = true;
    note.hidden = false;
    legend.hidden = false;
    viewport.hidden = false;
    tools.hidden = false;
    tabbar.hidden = false;
    config = loadRackConfig(mode);
    active = config.tabs.find((t) => t.id === config.activeId) ?? config.tabs[0]!;
    selectedSlot = null;
    ensureThree();
    resize();
    frameCamera();
    buildFelt();
    buildBalls();
    refreshTransforms();
    buildTabbar();
    syncToolState();
  };

  const show = (): void => {
    refresh();
    if (mode && !running) {
      running = true;
      renderLoop();
    }
  };
  const hide = (): void => {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
  };

  return { element: root, show, hide };
};
